import { useState } from "react";
import CodeSnippet from "@/components/common/CodeSnippet";
import {
  REGIONS,
  buildPixelImage,
  buildPixelNoscript,
  buildPixelSnippet,
} from "../snippets";

// The two ways to install the pixel. They are not tiers of the same thing: the
// script collects the browser, the image collects only the request, and the
// image is the only option in an environment with no JavaScript at all.
const PIXEL_MODES = [
  {
    value: "script",
    label: "Script",
    hint: "Full collection — page, device and engagement. Needs JavaScript.",
  },
  {
    value: "image",
    label: "Image pixel",
    hint: "Request data only. Works with no JavaScript at all.",
  },
];

export default function PixelEmbed({ pixelUuid }) {
  const [region, setRegion] = useState(REGIONS[0].label);
  const [mode, setMode] = useState(PIXEL_MODES[0].value);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const origin =
    REGIONS.find((r) => r.label === region)?.origin ?? REGIONS[0].origin;
  const activeMode =
    PIXEL_MODES.find((m) => m.value === mode) ?? PIXEL_MODES[0];

  return (
    <div className="flex flex-col gap-3">
      {REGIONS.length > 1 && (
        <label className="flex items-center gap-2 text-xs text-(--text-muted)">
          Region
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) px-2 py-1 text-xs text-(--text-primary)"
          >
            {REGIONS.map((r) => (
              <option key={r.label} value={r.label}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <div
        role="radiogroup"
        aria-label="Pixel install method"
        className="inline-flex self-start rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-0.5"
      >
        {PIXEL_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={mode === m.value}
            onClick={() => setMode(m.value)}
            className={`rounded-(--radius-sm) px-3 py-1 text-xs transition-colors ${
              mode === m.value
                ? "bg-(--surface-muted) font-semibold text-(--text-primary)"
                : "text-(--text-secondary) hover:text-(--text-primary)"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <p className="text-[12px] text-(--text-muted)">{activeMode.hint}</p>

      {mode === "script" ? (
        <>
          <CodeSnippet
            label="Paste before </head>"
            code={buildPixelSnippet(pixelUuid, origin)}
          />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="self-start border-0 bg-transparent text-xs text-(--text-secondary) underline decoration-dotted underline-offset-2 hover:text-(--text-primary)"
          >
            {showAdvanced ? "Hide" : "Show"} fallback &amp; consent options
          </button>

          {showAdvanced && (
            <div className="flex flex-col gap-3">
              <CodeSnippet
                label="No-JavaScript fallback (optional)"
                code={buildPixelNoscript(pixelUuid, origin)}
              />
              <CodeSnippet
                label="Suppress from a consent manager"
                code={`window.adlywise = window.adlywise || [];\nwindow.adlywise.push(["consent", false]);`}
              />
              <p className="text-[12px] text-(--text-muted)">
                Push <code>[&quot;consent&quot;, false]</code> before or after
                the tag loads, or add <code>data-consent=&quot;deny&quot;</code>{" "}
                to the script tag. Either stops collection.
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <CodeSnippet
            label="Paste anywhere in the page"
            code={buildPixelImage(pixelUuid, origin)}
          />
          <p className="text-[12px] text-(--text-muted)">
            Collects only what the image request itself carries: IP, user agent,
            language, the browser&apos;s client hints, and the page URL from the
            Referer header. No screen, timezone, fingerprint or engagement data
            — those need the script.
          </p>
          <p className="text-[12px] text-(--text-muted)">
            Where the page sends no Referer (email, AMP,{" "}
            <code>Referrer-Policy: no-referrer</code>), append{" "}
            <code>?u=&lt;page-url&gt;</code> to the image URL. Any other query
            parameter is ignored, so a cache-buster is safe to add.
          </p>
          <p className="text-[12px] text-(--text-muted)">
            An image cannot read a consent manager, so gate it where the page is
            rendered. Use one option per page — to cover both cases, keep the
            script and add the image inside <code>&lt;noscript&gt;</code>, or
            each page view is counted twice.
          </p>
        </>
      )}
    </div>
  );
}
